import { CheckSquare, Square, Link2, MapPin, FolderInput, Edit, Trash2 } from 'lucide-react'
import type { CaseEndpoint } from '../../../types/case-container'
import { getRoleDisplayLabel } from '../../../types/case-container'

const DEVICE_TYPE_COLOURS: Record<string, string> = {
  workstation: 'bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-400',
  server: 'bg-purple-100 text-purple-700 dark:bg-purple-950/50 dark:text-purple-400',
  domain_controller: 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400',
  file_server: 'bg-green-100 text-green-700 dark:bg-green-950/50 dark:text-green-400',
  mail_server: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-950/50 dark:text-cyan-400',
  web_server: 'bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-400',
  database_server: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-400',
  network_device: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
  mobile_device: 'bg-pink-100 text-pink-700 dark:bg-pink-950/50 dark:text-pink-400',
}

function importanceClass(importance: string): string {
  if (importance === 'critical')
    return 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-400'
  if (importance === 'high')
    return 'bg-orange-100 text-orange-700 dark:bg-orange-950/50 dark:text-orange-400'
  if (importance === 'medium')
    return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950/50 dark:text-yellow-400'
  return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400'
}

function RowIdentityCells({
  endpoint,
  visibleColumns,
}: {
  endpoint: CaseEndpoint
  visibleColumns: Set<string>
}): React.ReactElement {
  const deviceColour =
    DEVICE_TYPE_COLOURS[endpoint.deviceType] ?? DEVICE_TYPE_COLOURS.network_device ?? ''
  return (
    <>
      {visibleColumns.has('hostname') && (
        <td className="px-4 py-3">
          <div className="font-medium text-slate-900 dark:text-slate-100">{endpoint.hostname}</div>
          {endpoint.notes && (
            <div className="max-w-xs truncate text-xs text-slate-500 dark:text-slate-400">
              {endpoint.notes}
            </div>
          )}
        </td>
      )}
      {visibleColumns.has('type') && (
        <td className="px-4 py-3">
          <span className={`inline-flex rounded px-2 py-1 text-xs font-medium ${deviceColour}`}>
            {endpoint.deviceType.replaceAll('_', ' ')}
          </span>
        </td>
      )}
    </>
  )
}

function RowTextCells({
  endpoint,
  visibleColumns,
}: {
  endpoint: CaseEndpoint
  visibleColumns: Set<string>
}): React.ReactElement {
  const textCells: Array<{ key: string; value: string }> = [
    { key: 'ipAddresses', value: endpoint.ipAddresses?.join(', ') ?? '-' },
    { key: 'domain', value: endpoint.domain ?? '-' },
    { key: 'os', value: endpoint.operatingSystem ?? '-' },
    { key: 'osVersion', value: endpoint.osVersion ?? '-' },
  ]
  return (
    <>
      {textCells
        .filter((cell) => visibleColumns.has(cell.key))
        .map((cell) => (
          <td key={cell.key} className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
            {cell.value}
          </td>
        ))}
      {visibleColumns.has('ouPath') && (
        <td
          className="max-w-xs truncate px-4 py-3 text-sm text-slate-600 dark:text-slate-400"
          title={endpoint.ouPath ?? ''}
        >
          {endpoint.ouPath ?? '-'}
        </td>
      )}
    </>
  )
}

function RowSimpleCells({
  endpoint,
  visibleColumns,
}: {
  endpoint: CaseEndpoint
  visibleColumns: Set<string>
}): React.ReactElement {
  return (
    <>
      <RowIdentityCells endpoint={endpoint} visibleColumns={visibleColumns} />
      <RowTextCells endpoint={endpoint} visibleColumns={visibleColumns} />
    </>
  )
}

function RowBadgeCells({
  endpoint,
  visibleColumns,
  relationshipCount,
}: {
  endpoint: CaseEndpoint
  visibleColumns: Set<string>
  relationshipCount: number
}): React.ReactElement {
  const rolesList = endpoint.roles.map((role) => getRoleDisplayLabel(role)).join(', ')
  return (
    <>
      {visibleColumns.has('importance') && (
        <td className="px-4 py-3 text-center">
          {endpoint.importance !== 'unknown' ? (
            <span
              className={`inline-flex rounded px-2 py-1 text-xs font-medium ${importanceClass(endpoint.importance)}`}
            >
              {endpoint.importance}
            </span>
          ) : (
            <span className="text-xs text-slate-400 dark:text-slate-500">-</span>
          )}
        </td>
      )}
      {visibleColumns.has('roles') && (
        <td className="max-w-xs px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
          {endpoint.roles.length > 0 ? (
            <span className="truncate" title={rolesList}>
              {rolesList}
            </span>
          ) : (
            <span className="text-xs text-slate-400 dark:text-slate-500">-</span>
          )}
        </td>
      )}
      {visibleColumns.has('links') && (
        <td className="px-4 py-3 text-center">
          {relationshipCount > 0 ? (
            <span className="inline-flex items-center gap-1 rounded bg-lime-100 px-2 py-1 text-xs font-medium text-[#5c6e94] dark:bg-lime-950/50 dark:text-lime-400">
              <Link2 className="h-3 w-3" />
              {relationshipCount}
            </span>
          ) : (
            <span className="text-xs text-slate-400 dark:text-slate-500">-</span>
          )}
        </td>
      )}
    </>
  )
}

export function RowDataCells({
  endpoint,
  visibleColumns,
  relationshipCount,
}: {
  endpoint: CaseEndpoint
  visibleColumns: Set<string>
  relationshipCount: number
}): React.ReactElement {
  return (
    <>
      <RowSimpleCells endpoint={endpoint} visibleColumns={visibleColumns} />
      <RowBadgeCells
        endpoint={endpoint}
        visibleColumns={visibleColumns}
        relationshipCount={relationshipCount}
      />
    </>
  )
}

export interface RowActionHandlers {
  handleLinkButtonContextMenu: (e: React.MouseEvent, ep: CaseEndpoint) => void
  handleMapButtonContextMenu: (e: React.MouseEvent, ep: CaseEndpoint) => void
  handleCollectionButtonContextMenu: (e: React.MouseEvent, ep: CaseEndpoint) => void
  handleDeleteContextMenu: (e: React.MouseEvent, ep: CaseEndpoint) => void
  handleDeleteSingle: (ep: CaseEndpoint) => void
  toggleMapSet: (id: string) => void
  toggleCollectionSet: (id: string) => void
  setLinkingEndpoint: (ep: CaseEndpoint) => void
  setEditingEndpoint: (ep: CaseEndpoint) => void
}

function RowPrimaryActions({
  endpoint,
  isInMapSet,
  isInCollectionSet,
  handlers,
}: {
  endpoint: CaseEndpoint
  isInMapSet: boolean
  isInCollectionSet: boolean
  handlers: Pick<
    RowActionHandlers,
    | 'handleLinkButtonContextMenu'
    | 'handleMapButtonContextMenu'
    | 'handleCollectionButtonContextMenu'
    | 'toggleMapSet'
    | 'toggleCollectionSet'
    | 'setLinkingEndpoint'
  >
}): React.ReactElement {
  return (
    <>
      <button
        onClick={() => {
          handlers.setLinkingEndpoint(endpoint)
        }}
        onContextMenu={(e) => {
          handlers.handleLinkButtonContextMenu(e, endpoint)
        }}
        className="rounded p-1 text-slate-400 hover:text-lime-600 dark:hover:text-lime-400"
        title="Create relationship (right-click for more options)"
      >
        <Link2 className="h-4 w-4" />
      </button>
      <button
        onClick={() => {
          handlers.toggleMapSet(endpoint.id)
        }}
        onContextMenu={(e) => {
          handlers.handleMapButtonContextMenu(e, endpoint)
        }}
        className={`rounded p-1 ${isInMapSet ? 'text-lime-600 hover:text-[#5c6e94] dark:text-lime-400 dark:hover:text-lime-300' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'}`}
        title={
          isInMapSet
            ? 'Remove from map (right-click for options)'
            : 'Add to map (right-click for options)'
        }
      >
        <MapPin className={`h-4 w-4 ${isInMapSet ? 'fill-current' : ''}`} />
      </button>
      <button
        onClick={() => {
          handlers.toggleCollectionSet(endpoint.id)
        }}
        onContextMenu={(e) => {
          handlers.handleCollectionButtonContextMenu(e, endpoint)
        }}
        className={`rounded p-1 ${isInCollectionSet ? 'text-lime-600 hover:text-[#5c6e94] dark:text-lime-400 dark:hover:text-lime-300' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'}`}
        title={
          isInCollectionSet
            ? 'Remove from collection set (right-click for options)'
            : 'Add to collection set (right-click for options)'
        }
      >
        <FolderInput className={`h-4 w-4 ${isInCollectionSet ? 'fill-current' : ''}`} />
      </button>
    </>
  )
}

function RowSecondaryActions({
  endpoint,
  deleting,
  bulkDeleting,
  handlers,
}: {
  endpoint: CaseEndpoint
  deleting: boolean
  bulkDeleting: boolean
  handlers: Pick<RowActionHandlers, 'handleDeleteContextMenu' | 'handleDeleteSingle' | 'setEditingEndpoint'>
}): React.ReactElement {
  return (
    <>
      <button
        onClick={() => {
          handlers.setEditingEndpoint(endpoint)
        }}
        className="rounded p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
        title="Edit"
      >
        <Edit className="h-4 w-4" />
      </button>
      <button
        onClick={() => {
          handlers.handleDeleteSingle(endpoint)
        }}
        onContextMenu={(e) => {
          handlers.handleDeleteContextMenu(e, endpoint)
        }}
        disabled={deleting || bulkDeleting}
        className="rounded p-1 text-slate-400 hover:text-red-600 dark:hover:text-red-400"
        title="Delete (right-click for more options)"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </>
  )
}

export function RowActionButtons({
  endpoint,
  isInMapSet,
  isInCollectionSet,
  deleting,
  bulkDeleting,
  handlers,
}: {
  endpoint: CaseEndpoint
  isInMapSet: boolean
  isInCollectionSet: boolean
  deleting: boolean
  bulkDeleting: boolean
  handlers: RowActionHandlers
}): React.ReactElement {
  return (
    <td className="px-4 py-3 text-right">
      <div className="flex items-center justify-end gap-2">
        <RowPrimaryActions
          endpoint={endpoint}
          isInMapSet={isInMapSet}
          isInCollectionSet={isInCollectionSet}
          handlers={handlers}
        />
        <RowSecondaryActions
          endpoint={endpoint}
          deleting={deleting}
          bulkDeleting={bulkDeleting}
          handlers={handlers}
        />
      </div>
    </td>
  )
}

export interface CaseInfrastructureTableRowProps {
  endpoint: CaseEndpoint
  visibleColumns: Set<string>
  isSelected: boolean
  isInMapSet: boolean
  isInCollectionSet: boolean
  relationshipCount: number
  deleting: boolean
  bulkDeleting: boolean
  handleCheckboxClick: (endpointId: string, e: React.MouseEvent) => void
  handleLinkButtonContextMenu: (e: React.MouseEvent, endpoint: CaseEndpoint) => void
  handleMapButtonContextMenu: (e: React.MouseEvent, endpoint: CaseEndpoint) => void
  handleCollectionButtonContextMenu: (e: React.MouseEvent, endpoint: CaseEndpoint) => void
  handleDeleteContextMenu: (e: React.MouseEvent, endpoint: CaseEndpoint) => void
  handleDeleteSingle: (endpoint: CaseEndpoint) => void
  toggleMapSet: (id: string) => void
  toggleCollectionSet: (id: string) => void
  setLinkingEndpoint: (e: CaseEndpoint) => void
  setEditingEndpoint: (e: CaseEndpoint) => void
}

export function CaseInfrastructureTableRow(
  props: CaseInfrastructureTableRowProps
): React.ReactElement {
  const {
    endpoint,
    visibleColumns,
    isSelected,
    isInMapSet,
    isInCollectionSet,
    relationshipCount,
    deleting,
    bulkDeleting,
    handleCheckboxClick,
    handleLinkButtonContextMenu,
    handleMapButtonContextMenu,
    handleCollectionButtonContextMenu,
    handleDeleteContextMenu,
    handleDeleteSingle,
    toggleMapSet,
    toggleCollectionSet,
    setLinkingEndpoint,
    setEditingEndpoint,
  } = props

  const actionHandlers = {
    handleLinkButtonContextMenu,
    handleMapButtonContextMenu,
    handleCollectionButtonContextMenu,
    handleDeleteContextMenu,
    handleDeleteSingle,
    toggleMapSet,
    toggleCollectionSet,
    setLinkingEndpoint,
    setEditingEndpoint,
  }

  return (
    <tr
      className={`hover:bg-slate-50 dark:hover:bg-slate-800/50 ${isSelected ? 'bg-[#4e5e82]/5 dark:bg-[#8a9bba]/10' : ''}`}
    >
      <td className="w-10 px-3 py-3">
        <button
          onClick={(e) => {
            handleCheckboxClick(endpoint.id, e)
          }}
          className="flex h-5 w-5 items-center justify-center rounded border border-slate-300 transition-colors hover:border-[#4e5e82] dark:border-slate-600 dark:hover:border-[#4e5e82]"
        >
          {isSelected ? (
            <CheckSquare className="h-4 w-4 text-lime-600 dark:text-lime-400" />
          ) : (
            <Square className="h-4 w-4 text-slate-400" />
          )}
        </button>
      </td>
      <RowDataCells
        endpoint={endpoint}
        visibleColumns={visibleColumns}
        relationshipCount={relationshipCount}
      />
      <RowActionButtons
        endpoint={endpoint}
        isInMapSet={isInMapSet}
        isInCollectionSet={isInCollectionSet}
        deleting={deleting}
        bulkDeleting={bulkDeleting}
        handlers={actionHandlers}
      />
    </tr>
  )
}
